import type { QueryClient } from "@tanstack/vue-query";
import { type Ref, watch } from "vue";
import { agentSessionScope } from "@/shared/agent-session-scope";

/** Quem está na sessão, pelo que importa ao cache: a pessoa e o workspace. */
export interface SessionIdentity {
	readonly userId: string;
	readonly tenantId: string;
}

/**
 * O CACHE DE CONSULTAS MORRE COM A SESSÃO.
 *
 * ── O DEFEITO ─────────────────────────────────────────────────────────────
 *
 * Sair de uma conta e entrar noutra, na mesma aba, mostrava a pessoa ANTERIOR
 * no menu do usuário (05/09/2026): o perfil vive numa consulta com cinco
 * minutos de validade e chave fixa, e "sair" nunca tocou no cache. Não era só
 * o perfil — qualquer consulta ainda válida (workspace, equipe, sino) servia a
 * resposta da conta que já tinha saído, até vencer.
 *
 * ── A REGRA ───────────────────────────────────────────────────────────────
 *
 * Quando a identidade da sessão muda de alguém para ninguém (saiu) ou para
 * outra pessoa ou outro workspace, o cache inteiro vai embora: consultas em
 * voo canceladas, respostas esquecidas. Entrar pela primeira vez (de ninguém
 * para alguém) não limpa nada, porque não há nada de outra conta lá — e limpar
 * apagaria o que a tela de entrada já pediu. Limpar aqui, e não em cada botão
 * "Sair", é o que garante que o terceiro caminho de sair (a troca de senha, um
 * futuro "sair de todos os aparelhos") não volte a esquecer.
 */
export function forgetCacheWhenIdentityChanges(
	client: Pick<QueryClient, "clear" | "cancelQueries">,
	identity: Ref<SessionIdentity | null>,
): () => void {
	return watch(
		() => (identity.value ? `${identity.value.tenantId}:${identity.value.userId}` : null),
		(now, before) => {
			agentSessionScope.value = now;
			if (before === null || before === undefined) return;
			if (now === before) return;
			void client.cancelQueries();
			client.clear();
		},
		{ immediate: true, flush: "sync" },
	);
}
